import React from "react";

const S = {
  wrapper:      { marginTop:'10px', marginLeft:'0px', border:'3px', padding:'40px', borderStyle:'solid', borderColor:'grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #2E75B6', paddingBottom:'8px', marginBottom:'24px', color:'#1F3864' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#2E75B6', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #2E75B6', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#EBF3FB', border:'2px solid #2E75B6', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#1F3864', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #2E75B6' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#2E75B6', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#f5f9ff' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#fff' },
  tdL:          { fontWeight:'600', color:'#1F3864', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#f8fbff', border:'1px solid #ccd9ea', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#1F3864', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Formula / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 1 — Types of Error: Systematic vs Random
//             Four target / scatter panels + summary strip
// ─────────────────────────────────────────────────────────────────
function DiagramTypesOfError() {
  const W = 900, H = 330;

  // Four target panels, each 200 wide
  const panDefs = [
    {
      title: 'Accurate & Precise',
      sub:   'Low systematic, low random',
      color: '#27ae60', fill: '#f0fff4',
      bias:  { dx:  0, dy:  0 },
      dots:  [{dx:2,dy:-1},{dx:-1,dy:2},{dx:1,dy:1},{dx:-2,dy:-1},{dx:0,dy:2}],
    },
    {
      title: 'Precise, Not Accurate',
      sub:   'Low random, high systematic',
      color: '#c0392b', fill: '#fff0f0',
      bias:  { dx: 22, dy:-18 },
      dots:  [{dx:21,dy:-17},{dx:23,dy:-19},{dx:22,dy:-16},{dx:20,dy:-18},{dx:24,dy:-20}],
    },
    {
      title: 'Accurate, Not Precise',
      sub:   'Low systematic, high random',
      color: '#e07b00', fill: '#fff7ee',
      bias:  { dx:  0, dy:  0 },
      dots:  [{dx:-18,dy:14},{dx:16,dy:-20},{dx:-8,dy:-22},{dx:22,dy:10},{dx:-14,dy:8}],
    },
    {
      title: 'Neither Accurate\nnor Precise',
      sub:   'High systematic, high random',
      color: '#9b59b6', fill: '#f5d0ff',
      bias:  { dx: 18, dy: 15 },
      dots:  [{dx:4,dy:28},{dx:32,dy:6},{dx:12,dy:-2},{dx:28,dy:22},{dx:-2,dy:18}],
    },
  ];

  const PAD = 10;
  const panW = (W - PAD * 2 - 6 * 3) / 4;  // ≈ 207
  const tR = 52;   // target ring radius
  const cx = panW / 2;
  const cy = 148;  // center of target within panel

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Types of Measurement Error — Systematic (Bias) vs Random (Precision)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {panDefs.map(({ title, sub, color, fill, bias, dots }, pi) => {
          const px = PAD + pi * (panW + 6);

          return (
            <g key={pi}>
              {/* Panel border */}
              <rect x={px} y={8} width={panW} height={H - 16} rx={5}
                fill={fill} stroke={color} strokeWidth="1.8"/>
              {/* Header */}
              <rect x={px+4} y={12} width={panW-8} height={34} rx={3} fill={color}/>
              {title.split('\n').map((ln, li) => (
                <text key={li} x={px + panW/2} y={27 + li*14}
                  textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">{ln}</text>
              ))}
              <text x={px + panW/2} y={54} textAnchor="middle" fontSize="9" fill={color}>{sub}</text>

              {/* Target rings — three concentric */}
              {[tR, tR*0.62, tR*0.30].map((r, ri) => (
                <circle key={ri} cx={px+cx} cy={cy} r={r}
                  fill="none"
                  stroke={ri===0?'#ccc':ri===1?'#bbb':'#aaa'}
                  strokeWidth="1.2"/>
              ))}
              {/* Crosshairs */}
              <line x1={px+cx-tR-4} y1={cy} x2={px+cx+tR+4} y2={cy} stroke="#e0e0e0" strokeWidth="0.8"/>
              <line x1={px+cx} y1={cy-tR-4} x2={px+cx} y2={cy+tR+4} stroke="#e0e0e0" strokeWidth="0.8"/>

              {/* True value (bullseye) */}
              <circle cx={px+cx} cy={cy} r={4} fill="#1F3864"/>
              <text x={px+cx+6} y={cy-6} fontSize="8" fill="#1F3864" fontWeight="700">True</text>

              {/* Measurement dots */}
              {dots.map((d, di) => (
                <circle key={di} cx={px+cx+d.dx} cy={cy+d.dy} r={4.5}
                  fill={color} stroke="#fff" strokeWidth="1" opacity="0.85"/>
              ))}

              {/* Centroid ring (mean of dots) */}
              {(() => {
                const mx = dots.reduce((s,d)=>s+d.dx,0)/dots.length;
                const my = dots.reduce((s,d)=>s+d.dy,0)/dots.length;
                return (
                  <circle cx={px+cx+mx} cy={cy+my} r={7}
                    fill="none" stroke={color} strokeWidth="1.8" strokeDasharray="3,2"/>
                );
              })()}

              {/* Bias arrow (only when bias is non-zero) */}
              {(bias.dx !== 0 || bias.dy !== 0) && (() => {
                const mx = dots.reduce((s,d)=>s+d.dx,0)/dots.length;
                const my = dots.reduce((s,d)=>s+d.dy,0)/dots.length;
                const x1 = px+cx, y1 = cy;
                const x2 = px+cx+mx, y2 = cy+my;
                return (
                  <g>
                    <defs>
                      <marker id={`arr${pi}`} markerWidth="6" markerHeight="6"
                        refX="5" refY="3" orient="auto">
                        <path d="M0,0 L0,6 L6,3 z" fill={color}/>
                      </marker>
                    </defs>
                    <line x1={x1} y1={y1} x2={x2} y2={y2}
                      stroke={color} strokeWidth="1.5"
                      markerEnd={`url(#arr${pi})`}/>
                    <text x={(x1+x2)/2+4} y={(y1+y2)/2-4} fontSize="9" fill={color} fontWeight="700">bias</text>
                  </g>
                );
              })()}

              {/* Bottom key stats */}
              <rect x={px+6} y={cy+tR+16} width={panW-12} height={H-16-(cy+tR+16)-8} rx={3}
                fill="#fff" stroke={color} strokeWidth="0.8" opacity="0.7"/>
              <text x={px+panW/2} y={cy+tR+30} textAnchor="middle" fontSize="9.5" fill={color} fontWeight="700">
                {pi===0 && 'Systematic error ≈ 0'}
                {pi===1 && 'Systematic error ≠ 0'}
                {pi===2 && 'Systematic error ≈ 0'}
                {pi===3 && 'Systematic error ≠ 0'}
              </text>
              <text x={px+panW/2} y={cy+tR+44} textAnchor="middle" fontSize="9" fill="#555">
                {pi===0 && 'Random error ≈ 0'}
                {pi===1 && 'Random error ≈ 0'}
                {pi===2 && 'Random error ≠ 0'}
                {pi===3 && 'Random error ≠ 0'}
              </text>
              <text x={px+panW/2} y={cy+tR+58} textAnchor="middle" fontSize="8.5" fill="#888">
                {pi===0 && 'Ideal case'}
                {pi===1 && 'Calibrate instrument'}
                {pi===2 && 'Increase n, average'}
                {pi===3 && 'Both corrections needed'}
              </text>
            </g>
          );
        })}

        {/* Bottom summary strip */}
        <rect x={PAD} y={H-22} width={W-PAD*2} height={16} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="0.8"/>
        <text x={W/2} y={H-11} textAnchor="middle" fontSize="9.5" fill="#1F3864" fontWeight="700">
          Accuracy = closeness to true value (absence of systematic error) · Precision = repeatability (absence of random error) · Both are required for good measurement
        </text>
      </svg>
      <div style={S.dCaption}>
        Systematic error (bias) shifts all measurements in the same direction and cannot be reduced by averaging — it must be found and corrected through calibration. Random error scatters measurements around the true value and can be reduced by repeating measurements and averaging. The dashed circle marks the centroid (mean) of the five dots; the arrow shows the bias vector.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Measurement Distribution: mean, std dev, uncertainty
//             Left: repeated measurements histogram + Normal overlay
//             Right: summary cards for mean, SD, SEM, uncertainty
// ─────────────────────────────────────────────────────────────────
function DiagramMeasurementDistribution() {
  const W = 900, H = 330;

  // 20 simulated measurements of a quantity with true value 9.81
  const meas = [9.74,9.78,9.80,9.82,9.84,9.76,9.83,9.79,9.85,9.77,
                9.81,9.83,9.75,9.82,9.80,9.78,9.86,9.81,9.79,9.83];
  const n = meas.length;
  const mean = meas.reduce((s,v)=>s+v,0)/n;               // ≈ 9.803
  const variance = meas.reduce((s,v)=>s+(v-mean)**2,0)/(n-1);
  const sd = Math.sqrt(variance);                           // sample SD
  const sem = sd / Math.sqrt(n);                            // SEM

  // Histogram bins: 9.72–9.88 in steps of 0.02
  const binEdges = [9.72,9.74,9.76,9.78,9.80,9.82,9.84,9.86,9.88];
  const bins = binEdges.slice(0,-1).map((lo,i)=>{
    const hi = binEdges[i+1];
    return { lo, hi, count: meas.filter(v=>v>=lo&&v<hi).length };
  });
  const maxCount = Math.max(...bins.map(b=>b.count));

  const aX=54, aY=32, aW=430, aH=210;
  const xMin=9.72, xMax=9.88;
  const toX = v => aX+(v-xMin)/(xMax-xMin)*aW;
  const toY = v => aY+aH-(v/maxCount)*aH;
  const binW = aW/bins.length;

  // Normal PDF overlay scaled to histogram
  const normpdf = (x,mu,sig) => (1/(sig*Math.sqrt(2*Math.PI)))*Math.exp(-0.5*((x-mu)/sig)**2);
  const scale = maxCount / normpdf(mean,mean,sd); // scale so peak aligns with tallest bin
  const curvePts = Array.from({length:100},(_,i)=>{
    const x = xMin+(xMax-xMin)*i/99;
    const y = normpdf(x,mean,sd)*scale;
    return `${toX(x).toFixed(1)},${toY(y).toFixed(1)}`;
  }).join(' ');

  const panX = 520, panW2 = W-panX-14;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>
        Distribution of Repeated Measurements — Mean, Standard Deviation, and Standard Error
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* ── LEFT PANEL ── */}
        <rect x={10} y={8} width={500} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <rect x={14} y={12} width={492} height={22} rx={3} fill="#1F3864"/>
        <text x={264} y={27} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">
          n=20 repeated measurements of g (m/s²) · True value = 9.81
        </text>

        {/* Grid lines */}
        {[1,2,3,4].map(v => (
          <g key={v}>
            <line x1={aX} y1={toY(v)} x2={aX+aW} y2={toY(v)} stroke="#eee" strokeWidth="0.8"/>
            <text x={aX-5} y={toY(v)+4} textAnchor="end" fontSize="9" fill="#aaa">{v}</text>
          </g>
        ))}

        {/* Axes */}
        <line x1={aX} y1={aY+aH} x2={aX+aW} y2={aY+aH} stroke="#888" strokeWidth="1.5"/>
        <line x1={aX} y1={aY}    x2={aX}    y2={aY+aH} stroke="#888" strokeWidth="1.5"/>

        {/* Histogram bars */}
        {bins.map(({ lo, hi, count },i) => {
          const bx = toX(lo)+1;
          const bw = toX(hi)-toX(lo)-2;
          const bh = (count/maxCount)*aH;
          const by = aY+aH-bh;
          return (
            <rect key={i} x={bx} y={by} width={bw} height={bh}
              fill="#2E75B6" opacity="0.55" rx={2}/>
          );
        })}

        {/* Normal curve overlay */}
        <polyline points={curvePts} fill="none" stroke="#2E75B6" strokeWidth="2.2"/>

        {/* True value line */}
        <line x1={toX(9.81)} y1={aY} x2={toX(9.81)} y2={aY+aH}
          stroke="#27ae60" strokeWidth="1.8" strokeDasharray="5,3"/>
        <text x={toX(9.81)+3} y={aY+12} fontSize="9.5" fill="#27ae60" fontWeight="700">True 9.81</text>

        {/* Mean line */}
        <line x1={toX(mean)} y1={aY} x2={toX(mean)} y2={aY+aH}
          stroke="#c0392b" strokeWidth="2" strokeDasharray="3,2"/>
        <text x={toX(mean)+3} y={aY+24} fontSize="9.5" fill="#c0392b" fontWeight="700">
          x̄={mean.toFixed(3)}
        </text>

        {/* ±1 SD shaded band */}
        <rect x={toX(mean-sd)} y={aY} width={toX(mean+sd)-toX(mean-sd)} height={aH}
          fill="#2E75B6" opacity="0.07"/>
        <line x1={toX(mean-sd)} y1={aY+aH-4} x2={toX(mean-sd)} y2={aY+aH+6}
          stroke="#2E75B6" strokeWidth="1.5"/>
        <line x1={toX(mean+sd)} y1={aY+aH-4} x2={toX(mean+sd)} y2={aY+aH+6}
          stroke="#2E75B6" strokeWidth="1.5"/>
        <line x1={toX(mean-sd)} y1={aY+aH+2} x2={toX(mean+sd)} y2={aY+aH+2}
          stroke="#2E75B6" strokeWidth="1.2"/>
        <text x={(toX(mean-sd)+toX(mean+sd))/2} y={aY+aH+14}
          textAnchor="middle" fontSize="8.5" fill="#2E75B6">±1σ = ±{sd.toFixed(4)}</text>

        {/* ±1 SEM bracket (taller, above histogram) */}
        {(() => {
          const x1=toX(mean-sem), x2=toX(mean+sem), yb=aY+aH-6;
          return (
            <g>
              <line x1={x1} y1={yb-12} x2={x2} y2={yb-12} stroke="#c0392b" strokeWidth="2"/>
              <line x1={x1} y1={yb-16} x2={x1} y2={yb-8}  stroke="#c0392b" strokeWidth="1.5"/>
              <line x1={x2} y1={yb-16} x2={x2} y2={yb-8}  stroke="#c0392b" strokeWidth="1.5"/>
              <text x={(x1+x2)/2} y={yb-18} textAnchor="middle" fontSize="8" fill="#c0392b" fontWeight="700">
                ±SEM={sem.toFixed(4)}
              </text>
            </g>
          );
        })()}

        {/* X-axis ticks */}
        {binEdges.map(v=>(
          <g key={v}>
            <line x1={toX(v)} y1={aY+aH} x2={toX(v)} y2={aY+aH+5} stroke="#aaa" strokeWidth="1"/>
            <text x={toX(v)} y={aY+aH+16} textAnchor="middle" fontSize="8.5" fill="#666">{v.toFixed(2)}</text>
          </g>
        ))}
        <text x={aX+aW/2} y={aY+aH+30} textAnchor="middle" fontSize="11" fill="#555">Measured value (m/s²)</text>
        <text x={aX-38} y={aY+aH/2} textAnchor="middle" fontSize="10" fill="#555"
          transform={`rotate(-90,${aX-38},${aY+aH/2})`}>Count</text>

        {/* ── RIGHT PANEL ── */}
        <rect x={panX} y={8} width={panW2} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX+panW2/2} y={26} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Summary Statistics
        </text>

        {[
          { y:34,  bg:'#EBF3FB', bc:'#2E75B6',
            label:'Sample mean x̄',
            val: mean.toFixed(4)+' m/s²',
            note:'Best estimate of true value' },
          { y:100, bg:'#fff7ee', bc:'#e07b00',
            label:'Sample SD  s',
            val: sd.toFixed(4)+' m/s²',
            note:'Spread of one measurement' },
          { y:166, bg:'#fff0f0', bc:'#c0392b',
            label:'Std Error SEM',
            val: sem.toFixed(4)+' m/s²',
            note:'Uncertainty in x̄ = s/√n' },
          { y:232, bg:'#f0fff4', bc:'#27ae60',
            label:'Result ± uncertainty',
            val: `${mean.toFixed(3)} ± ${sem.toFixed(3)}`,
            note:'68% coverage (k=1)' },
        ].map(({ y, bg, bc, label, val, note }) => (
          <g key={y}>
            <rect x={panX+8} y={y} width={panW2-16} height={58} rx={4} fill={bg} stroke={bc} strokeWidth="1.3"/>
            <text x={panX+18} y={y+16} fontSize="10.5" fill={bc} fontWeight="700">{label}</text>
            <text x={panX+18} y={y+34} fontSize="13" fill="#1F3864"
              fontFamily="'Courier New',monospace" fontWeight="700">{val}</text>
            <text x={panX+18} y={y+50} fontSize="9" fill="#666">{note}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Repeated measurements follow a Normal distribution centred on the true value (if systematic error is absent). The sample standard deviation s describes the spread of individual measurements; the standard error of the mean SEM = s/√n describes the uncertainty in the mean itself. SEM shrinks as √n, so quadrupling measurements halves the uncertainty in x̄.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Error Propagation: adding, multiplying, general formula
//             Left: visual bar decomposition  Right: formula cards
// ─────────────────────────────────────────────────────────────────
function DiagramErrorPropagation() {
  const W = 900, H = 340;

  // Example: f = A + B  and  g = A × B
  // A = 5.0 ± 0.3,  B = 3.0 ± 0.2
  const A=5.0, dA=0.3, B=3.0, dB=0.2;

  // Addition/subtraction: δ(A+B) = √(δA² + δB²)
  const fAdd    = A+B;
  const dfAdd   = Math.sqrt(dA**2+dB**2);
  const dfAddP  = dfAdd/fAdd*100;

  // Multiplication: δ(A×B)/(A×B) = √((δA/A)² + (δB/B)²)
  const fMul    = A*B;
  const dfMulR  = Math.sqrt((dA/A)**2+(dB/B)**2);
  const dfMul   = fMul*dfMulR;

  // Power: f=A^n, δf/f = n·δA/A  (example n=2)
  const n2=2;
  const fPow   = A**n2;
  const dfPow  = n2*(dA/A)*fPow;

  // Bar chart area
  const barData = [
    { label:'A', val:A, err:dA, color:'#2E75B6' },
    { label:'B', val:B, err:dB, color:'#27ae60' },
    { label:'A+B', val:fAdd, err:dfAdd, color:'#e07b00' },
    { label:'A×B', val:fMul/fMul*8.5, err:dfMul/fMul*8.5, color:'#c0392b' }, // scaled to fit
  ];

  const aX=54, aY=34, aW=400, aH=200;
  const yMax=10;
  const toY = v => aY+aH-(v/yMax)*aH;
  const barW=aW/barData.length - 18;

  const panX=500, panW=W-panX-14;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>
        Uncertainty Propagation — Addition, Multiplication, and General Formula (A=5.0±0.3, B=3.0±0.2)
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* LEFT PANEL */}
        <rect x={10} y={8} width={480} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <rect x={14} y={12} width={472} height={22} rx={3} fill="#1F3864"/>
        <text x={254} y={27} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">
          Propagated uncertainties shown as error bars (proportional)
        </text>

        {/* Gridlines */}
        {[2,4,6,8].map(v=>(
          <g key={v}>
            <line x1={aX} y1={toY(v)} x2={aX+aW} y2={toY(v)} stroke="#eee" strokeWidth="0.8"/>
            <text x={aX-5} y={toY(v)+4} textAnchor="end" fontSize="9" fill="#bbb">{v}</text>
          </g>
        ))}
        <line x1={aX} y1={aY+aH} x2={aX+aW} y2={aY+aH} stroke="#888" strokeWidth="1.5"/>
        <line x1={aX} y1={aY}    x2={aX}    y2={aY+aH} stroke="#888" strokeWidth="1"/>

        {/* Bars + error bars */}
        {barData.map(({ label, val, err, color }, i) => {
          const bx = aX + i*(aW/barData.length) + 10;
          const bh = (val/yMax)*aH;
          const by = aY+aH-bh;
          const ex = bx+barW/2;
          const ey_mid = by;
          const ey_up  = Math.max(aY, toY(val+err));
          const ey_dn  = Math.min(aY+aH, toY(val-err));

          return (
            <g key={i}>
              {/* Bar */}
              <rect x={bx} y={by} width={barW} height={bh}
                fill={color} opacity="0.7" rx={3}/>
              {/* Error bar */}
              <line x1={ex} y1={ey_up} x2={ex} y2={ey_dn}
                stroke={color} strokeWidth="2.2"/>
              <line x1={ex-7} y1={ey_up} x2={ex+7} y2={ey_up}
                stroke={color} strokeWidth="1.8"/>
              <line x1={ex-7} y1={ey_dn} x2={ex+7} y2={ey_dn}
                stroke={color} strokeWidth="1.8"/>
              {/* Value label */}
              <text x={ex} y={ey_up-6} textAnchor="middle" fontSize="9.5"
                fill={color} fontWeight="700">
                {label==='A×B' ? `${fMul.toFixed(1)}` : label==='A+B' ? fAdd.toFixed(1) : label==='A'?A:B}
              </text>
              {/* Error label */}
              <text x={ex} y={ey_dn+14} textAnchor="middle" fontSize="8.5" fill={color}>
                ±{label==='A×B' ? dfMul.toFixed(2) : label==='A+B' ? dfAdd.toFixed(3) : label==='A'?dA:dB}
              </text>
              {/* Bar label */}
              <text x={ex} y={aY+aH+18} textAnchor="middle" fontSize="11" fill={color} fontWeight="700">
                {label}
              </text>
            </g>
          );
        })}

        {/* Relative uncertainty labels */}
        {[
          {i:0, pct:(dA/A*100).toFixed(1)+'%'},
          {i:1, pct:(dB/B*100).toFixed(1)+'%'},
          {i:2, pct:dfAddP.toFixed(1)+'%'},
          {i:3, pct:(dfMulR*100).toFixed(1)+'%'},
        ].map(({i,pct})=>{
          const ex = aX + i*(aW/barData.length) + 10 + barW/2;
          return (
            <text key={i} x={ex} y={aY+aH+32} textAnchor="middle"
              fontSize="8.5" fill="#888">{pct} rel.</text>
          );
        })}

        <text x={aX-38} y={aY+aH/2} textAnchor="middle" fontSize="10" fill="#555"
          transform={`rotate(-90,${aX-38},${aY+aH/2})`}>Value</text>

        {/* Propagation comparison box */}
        <rect x={14} y={H-42} width={472} height={30} rx={3} fill="#EBF3FB" stroke="#2E75B6"/>
        <text x={254} y={H-28} textAnchor="middle" fontSize="10" fill="#1F3864" fontWeight="700">
          δ(A+B) = √(δA²+δB²) = {dfAdd.toFixed(3)}  ·  δ(A×B)/(A×B) = √((δA/A)²+(δB/B)²) = {(dfMulR).toFixed(3)}  →  δ(A×B) = {dfMul.toFixed(2)}
        </text>
        <text x={254} y={H-15} textAnchor="middle" fontSize="9" fill="#555">
          Relative uncertainty in product = {(dfMulR*100).toFixed(1)}%  (larger than either input: {(dA/A*100).toFixed(1)}% and {(dB/B*100).toFixed(1)}%)
        </text>

        {/* RIGHT PANEL */}
        <rect x={panX} y={8} width={panW} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX+panW/2} y={26} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Propagation Rules
        </text>

        {[
          { y:32,  bg:'#EBF3FB', bc:'#2E75B6', title:'Add / Subtract',
            lines:['f = A ± B','δf = √(δA² + δB²)','Uncertainties add in','quadrature — never cancel'] },
          { y:122, bg:'#fff7ee', bc:'#e07b00', title:'Multiply / Divide',
            lines:['f = A·B  or  A/B','δf/f = √((δA/A)²+(δB/B)²)','Relative uncertainties','add in quadrature'] },
          { y:212, bg:'#f0fff4', bc:'#27ae60', title:'General (Taylor)',
            lines:['δf²=Σ(∂f/∂xᵢ·δxᵢ)²','Partial derivatives','give sensitivity to','each input variable'] },
        ].map(({ y, bg, bc, title, lines }) => (
          <g key={y}>
            <rect x={panX+8} y={y} width={panW-16} height={84} rx={4} fill={bg} stroke={bc} strokeWidth="1.3"/>
            <text x={panX+18} y={y+16} fontSize="12" fill={bc} fontWeight="700">{title}</text>
            {lines.map((ln, j) => (
              <text key={j} x={panX+18} y={y+32+j*16} fontSize="10.5"
                fill={j<2?'#1F3864':'#555'}
                fontFamily={j<2?"'Courier New',monospace":"'Georgia',serif"}
                fontWeight={j<2?'700':'400'}>{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Uncertainties from independent sources combine in quadrature (add their squares then take the square root), not simply. For sums and differences the absolute uncertainties combine; for products and quotients the relative uncertainties combine. The general formula via partial derivatives handles any function of multiple measurements, weighting each input's uncertainty by how sensitive f is to that input.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Coverage Intervals: 1σ 2σ 3σ bands on a Normal curve
//             Left: shaded normal PDF   Right: interval table cards
// ─────────────────────────────────────────────────────────────────
function DiagramCoverageIntervals() {
  const W = 900, H = 320;

  const normpdf = x => (1/Math.sqrt(2*Math.PI))*Math.exp(-0.5*x*x);
  const xMin=-4, xMax=4;

  const aX=54, aY=28, aW=490, aH=200;
  const toX = x => aX+(x-xMin)/(xMax-xMin)*aW;
  const maxDens = normpdf(0);
  const toY = y => aY+aH-(y/maxDens)*aH;

  const allPts = Array.from({length:120},(_,i)=>{
    const x=xMin+(xMax-xMin)*i/119; return {x,y:normpdf(x)};
  });
  const polyAll = allPts.map(({x,y})=>`${toX(x).toFixed(1)},${toY(y).toFixed(1)}`).join(' ');

  // Shaded regions: 1σ, 2σ, 3σ  (each includes the inner)
  const bands = [
    { lo:-3, hi:3, color:'#9b59b6', opacity:0.12, label:'99.73%  k=3' },
    { lo:-2, hi:2, color:'#e07b00', opacity:0.18, label:'95.45%  k=2' },
    { lo:-1, hi:1, color:'#2E75B6', opacity:0.28, label:'68.27%  k=1' },
  ];

  const mkShade = (lo,hi) => {
    const seg = allPts.filter(p=>p.x>=lo&&p.x<=hi);
    return [
      `${toX(lo).toFixed(1)},${(aY+aH).toFixed(1)}`,
      ...seg.map(({x,y})=>`${toX(x).toFixed(1)},${toY(y).toFixed(1)}`),
      `${toX(hi).toFixed(1)},${(aY+aH).toFixed(1)}`,
    ].join(' ');
  };

  const panX=580, panW=W-panX-14;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Coverage Intervals — k·σ Bands and Probability Content for a Normal Distribution</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* LEFT PANEL */}
        <rect x={10} y={8} width={560} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <rect x={14} y={12} width={552} height={22} rx={3} fill="#1F3864"/>
        <text x={294} y={27} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">
          Normal distribution N(μ,σ²) — shaded areas show probability content
        </text>

        {/* Grid */}
        {[0.1,0.2,0.3,0.4].map(v=>(
          <g key={v}>
            <line x1={aX} y1={toY(v)} x2={aX+aW} y2={toY(v)} stroke="#eee" strokeWidth="0.8"/>
            <text x={aX-4} y={toY(v)+4} textAnchor="end" fontSize="8.5" fill="#bbb">{v.toFixed(1)}</text>
          </g>
        ))}
        <line x1={aX} y1={aY+aH} x2={aX+aW} y2={aY+aH} stroke="#aaa" strokeWidth="1.5"/>
        <line x1={aX} y1={aY}    x2={aX}    y2={aY+aH} stroke="#aaa" strokeWidth="1"/>

        {/* Shaded bands — draw outermost first */}
        {bands.map(({lo,hi,color,opacity})=>(
          <polygon key={`${lo}`} points={mkShade(lo,hi)} fill={color} opacity={opacity}/>
        ))}

        {/* Normal curve */}
        <polyline points={polyAll} fill="none" stroke="#2E75B6" strokeWidth="2.5"/>

        {/* σ tick marks and labels */}
        {[-3,-2,-1,0,1,2,3].map(v=>(
          <g key={v}>
            <line x1={toX(v)} y1={aY+aH} x2={toX(v)} y2={aY+aH+5} stroke="#aaa" strokeWidth="1"/>
            <text x={toX(v)} y={aY+aH+16} textAnchor="middle" fontSize="10" fill="#666">
              {v===0?'μ':v>0?`+${v}σ`:`${v}σ`}
            </text>
          </g>
        ))}

        {/* Band boundary lines */}
        {[-3,-2,-1,1,2,3].map(v=>(
          <line key={v} x1={toX(v)} y1={aY} x2={toX(v)} y2={aY+aH}
            stroke={Math.abs(v)===1?'#2E75B6':Math.abs(v)===2?'#e07b00':'#9b59b6'}
            strokeWidth="1.2" strokeDasharray="4,3" opacity="0.7"/>
        ))}

        {/* Percentage labels inside bands */}
        {[
          { x:0,      y: normpdf(0)*0.35, label:'68.27%', color:'#2E75B6' },
          { x:1.55,   y: normpdf(1.55)*0.9, label:'13.59%', color:'#e07b00' },
          { x:-1.55,  y: normpdf(1.55)*0.9, label:'13.59%', color:'#e07b00' },
          { x:2.55,   y: normpdf(2.55)*2.5, label:'2.14%',  color:'#9b59b6' },
          { x:-2.55,  y: normpdf(2.55)*2.5, label:'2.14%',  color:'#9b59b6' },
        ].map(({x,y,label,color},i)=>(
          <text key={i} x={toX(x)} y={toY(y)} textAnchor="middle"
            fontSize={i===0?11:9} fill={color} fontWeight="700">{label}</text>
        ))}

        <text x={aX+aW/2} y={aY+aH+30} textAnchor="middle" fontSize="11" fill="#555">
          Deviation from mean (in units of σ)
        </text>
        <text x={aX-38} y={aY+aH/2} textAnchor="middle" fontSize="10" fill="#555"
          transform={`rotate(-90,${aX-38},${aY+aH/2})`}>Probability density</text>

        {/* RIGHT PANEL */}
        <rect x={panX} y={8} width={panW} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX+panW/2} y={26} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">
          Coverage Table
        </text>

        {[
          { y:32,  bg:'#EBF3FB', bc:'#2E75B6',  k:'k = 1', cov:'68.27%', use:'Standard uncertainty (U68)' },
          { y:96,  bg:'#fff7ee', bc:'#e07b00',  k:'k = 2', cov:'95.45%', use:'Expanded uncertainty (U95)' },
          { y:160, bg:'#f5d0ff', bc:'#9b59b6',  k:'k = 3', cov:'99.73%', use:'Near-certain range (U99)' },
          { y:224, bg:'#f0fff4', bc:'#27ae60',  k:'k = 1.96', cov:'95.00%', use:'Exact 95% CI (z=1.96)' },
        ].map(({ y, bg, bc, k, cov, use }) => (
          <g key={y}>
            <rect x={panX+8} y={y} width={panW-16} height={56} rx={4} fill={bg} stroke={bc} strokeWidth="1.3"/>
            <text x={panX+18} y={y+18} fontSize="13" fill={bc}
              fontFamily="'Courier New',monospace" fontWeight="700">{k}</text>
            <text x={panX+18} y={y+35} fontSize="14" fill="#1F3864" fontWeight="700">{cov}</text>
            <text x={panX+18} y={y+50} fontSize="9" fill="#666">{use}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        For a Normal distribution, intervals of ±kσ around the mean contain a fixed fraction of the probability. The k=1 interval (68.27%) is the standard uncertainty; k=2 (~95%) is the expanded uncertainty commonly reported in metrology; k=3 (~99.73%) is used when near-certainty is required. Note: k=1.96 gives exactly 95%, as used in confidence intervals.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Error Bars: how to read and plot them
//             Three side-by-side mini charts: SD, SEM, 95% CI
// ─────────────────────────────────────────────────────────────────
function DiagramErrorBars() {
  const W = 900, H = 330;

  // Three groups A, B, C with different means
  const groups = [
    { label:'Group A', mean:4.2, sd:1.1, n:10 },
    { label:'Group B', mean:6.5, sd:0.9, n:10 },
    { label:'Group C', mean:5.1, sd:1.4, n:10 },
  ];

  const panDefs = [
    { title:'Error bars = ±1 SD', sub:'Spread of individual data', color:'#2E75B6', fill:'#EBF3FB',
      errFn: g => g.sd },
    { title:'Error bars = ±1 SEM', sub:'Uncertainty in group mean', color:'#27ae60', fill:'#f0fff4',
      errFn: g => g.sd/Math.sqrt(g.n) },
    { title:'Error bars = ±95% CI', sub:'Interval containing true mean', color:'#c0392b', fill:'#fff0f0',
      errFn: g => 1.96*g.sd/Math.sqrt(g.n) },
  ];

  const panW = (W-28)/3;
  const aH=190, aY_off=46;
  const yMax=9, yMin=0;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Three Types of Error Bars — SD, SEM, and 95% CI (same data, three representations)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {panDefs.map(({ title, sub, color, fill, errFn }, pi) => {
          const px = 10 + pi*(panW+4);
          const aX = px+40, aW=panW-56;
          const aY = aY_off;
          const barSpacing = aW/groups.length;
          const barW2 = barSpacing*0.38;
          const toY = v => aY+aH-(v-yMin)/(yMax-yMin)*aH;

          return (
            <g key={pi}>
              {/* Panel */}
              <rect x={px} y={8} width={panW} height={H-16} rx={5} fill={fill} stroke={color} strokeWidth="1.8"/>
              <rect x={px+4} y={12} width={panW-8} height={30} rx={3} fill={color}/>
              <text x={px+panW/2} y={26} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">{title}</text>
              <text x={px+panW/2} y={38} textAnchor="middle" fontSize="9.5" fill="#ffffffcc">{sub}</text>

              {/* Plot area */}
              <rect x={aX} y={aY} width={aW} height={aH} rx={3} fill="#fff" stroke="#ddd"/>

              {/* Grid */}
              {[2,4,6,8].map(v=>(
                <g key={v}>
                  <line x1={aX} y1={toY(v)} x2={aX+aW} y2={toY(v)} stroke="#f0f0f0" strokeWidth="0.8"/>
                  <text x={aX-4} y={toY(v)+4} textAnchor="end" fontSize="8" fill="#bbb">{v}</text>
                </g>
              ))}
              <line x1={aX} y1={aY+aH} x2={aX+aW} y2={aY+aH} stroke="#aaa" strokeWidth="1.2"/>

              {/* Bars + error bars */}
              {groups.map((g,gi) => {
                const bx = aX + gi*barSpacing + (barSpacing-barW2)/2;
                const by = toY(g.mean);
                const bh = aY+aH-by;
                const ex = bx+barW2/2;
                const err = errFn(g);
                const ey_up = Math.max(aY+2, toY(g.mean+err));
                const ey_dn = Math.min(aY+aH-2, toY(g.mean-err));

                return (
                  <g key={gi}>
                    {/* Bar */}
                    <rect x={bx} y={by} width={barW2} height={bh}
                      fill={color} opacity="0.65" rx={2}/>
                    {/* Error bar */}
                    <line x1={ex} y1={ey_up} x2={ex} y2={ey_dn}
                      stroke={color} strokeWidth="2"/>
                    <line x1={ex-6} y1={ey_up} x2={ex+6} y2={ey_up}
                      stroke={color} strokeWidth="1.8"/>
                    <line x1={ex-6} y1={ey_dn} x2={ex+6} y2={ey_dn}
                      stroke={color} strokeWidth="1.8"/>
                    {/* Mean dot */}
                    <circle cx={ex} cy={toY(g.mean)} r={3.5}
                      fill="#fff" stroke={color} strokeWidth="1.5"/>
                    {/* Group label */}
                    <text x={ex} y={aY+aH+14} textAnchor="middle" fontSize="9.5"
                      fill={color} fontWeight="700">{g.label.split(' ')[1]}</text>
                    {/* Error value */}
                    <text x={ex} y={ey_up-6} textAnchor="middle" fontSize="8"
                      fill={color}>±{err.toFixed(2)}</text>
                  </g>
                );
              })}

              {/* y label */}
              <text x={aX-28} y={aY+aH/2} textAnchor="middle" fontSize="9" fill="#888"
                transform={`rotate(-90,${aX-28},${aY+aH/2})`}>Measurement</text>

              {/* Key message */}
              <rect x={px+6} y={aY+aH+24} width={panW-12} height={H-aY-aH-32} rx={3}
                fill="#fff" stroke={color} strokeWidth="0.8"/>
              {pi===0 && (
                <>
                  <text x={px+panW/2} y={aY+aH+38} textAnchor="middle" fontSize="9.5" fill={color} fontWeight="700">SD does not shrink with n</text>
                  <text x={px+panW/2} y={aY+aH+52} textAnchor="middle" fontSize="9" fill="#555">Describes individual data spread</text>
                  <text x={px+panW/2} y={aY+aH+65} textAnchor="middle" fontSize="9" fill="#555">Overlapping bars ≠ no difference</text>
                </>
              )}
              {pi===1 && (
                <>
                  <text x={px+panW/2} y={aY+aH+38} textAnchor="middle" fontSize="9.5" fill={color} fontWeight="700">SEM = SD/√n → shrinks with n</text>
                  <text x={px+panW/2} y={aY+aH+52} textAnchor="middle" fontSize="9" fill="#555">Describes precision of mean</text>
                  <text x={px+panW/2} y={aY+aH+65} textAnchor="middle" fontSize="9" fill="#555">Non-overlap suggests significance</text>
                </>
              )}
              {pi===2 && (
                <>
                  <text x={px+panW/2} y={aY+aH+38} textAnchor="middle" fontSize="9.5" fill={color} fontWeight="700">95% CI = ±1.96·SEM</text>
                  <text x={px+panW/2} y={aY+aH+52} textAnchor="middle" fontSize="9" fill="#555">Non-overlap ≈ p&lt;0.05</text>
                  <text x={px+panW/2} y={aY+aH+65} textAnchor="middle" fontSize="9" fill="#555">Most informative for inference</text>
                </>
              )}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>
        The same data can look very different depending on which error bar is shown. SD bars are widest and describe the spread of individual measurements; SEM bars are narrower (SEM = SD/√n) and describe precision of the group mean; 95% CI bars are slightly wider than SEM and are most useful for inference. Always label which type of error bar is shown — the choice dramatically affects interpretation.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 6 — Monte Carlo Uncertainty Propagation
//             Left: input distributions  Right: output histogram
// ─────────────────────────────────────────────────────────────────
function DiagramMonteCarlo() {
  const W = 900, H = 330;

  // f = A * B / C     A~N(5,0.3²), B~N(3,0.2²), C~N(2,0.1²)
  // Analytical approximation: μ_f ≈ μA*μB/μC = 7.5
  // Relative var: (0.3/5)² + (0.2/3)² + (0.1/2)² = 0.0036+0.00444+0.0025 = 0.0105
  // σ_f ≈ 7.5*√0.0105 ≈ 0.768

  const muF = 5*3/2;           // 7.5
  const relVar = (0.3/5)**2 + (0.2/3)**2 + (0.1/2)**2;
  const sigF = muF*Math.sqrt(relVar); // ≈ 0.769

  const normpdf = (x,mu,sig) => (1/(sig*Math.sqrt(2*Math.PI)))*Math.exp(-0.5*((x-mu)/sig)**2);

  // Simulate output histogram using deterministic samples (reproducible)
  // Use a grid approximation instead of random: 200 output values
  // f_samples from convolution approximation → Normal(muF, sigF²)
  const N_bins = 16;
  const fMin = muF-3.5*sigF, fMax = muF+3.5*sigF;
  const binW2 = (fMax-fMin)/N_bins;
  const histBins = Array.from({length:N_bins},(_,i)=>{
    const lo=fMin+i*binW2, hi=lo+binW2;
    const mid=(lo+hi)/2;
    // Probability in this bin via Normal CDF approximation
    const count = Math.round(normpdf(mid,muF,sigF)*binW2*500);
    return { lo, hi, mid, count };
  });
  const maxCount = Math.max(...histBins.map(b=>b.count));

  // Left panel: three input distributions
  const inputs = [
    { label:'A', mu:5, sig:0.3, color:'#2E75B6', xRange:[3.8,6.2] },
    { label:'B', mu:3, sig:0.2, color:'#27ae60', xRange:[2.2,3.8] },
    { label:'C', mu:2, sig:0.1, color:'#e07b00', xRange:[1.5,2.5] },
  ];

  const LP_W=370, LP_H=H-20;
  const panH_each=(LP_H-20)/3;

  // Right panel: output histogram
  const RP_X=400, RP_W=W-RP_X-14;
  const aX=RP_X+50, aY=34, aW=RP_W-68, aH=200;
  const toX = v => aX+(v-fMin)/(fMax-fMin)*aW;
  const toY = v => aY+aH-(v/maxCount)*aH;

  // Output normal curve (overlay)
  const outCurvePts = Array.from({length:100},(_,i)=>{
    const x=fMin+(fMax-fMin)*i/99;
    const y=normpdf(x,muF,sigF)*binW2*500;
    return `${toX(x).toFixed(1)},${toY(y).toFixed(1)}`;
  }).join(' ');

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Monte Carlo Uncertainty Propagation — f = A·B/C with Independent Normal Inputs</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* LEFT: input panels */}
        <rect x={10} y={8} width={LP_W} height={LP_H} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <rect x={14} y={12} width={LP_W-8} height={22} rx={3} fill="#1F3864"/>
        <text x={14+LP_W/2} y={27} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">
          Input Distributions (Independent)
        </text>

        {inputs.map(({ label, mu, sig, color, xRange }, ii) => {
          const px=14, py=36+ii*panH_each;
          const iW=LP_W-28, iH=panH_each-8;
          const iAX=px+50, iAY=py+4, iAW=iW-60, iAH=iH-20;
          const [xLo,xHi]=xRange;
          const toIX = v => iAX+(v-xLo)/(xHi-xLo)*iAW;
          const maxD=normpdf(mu,mu,sig);
          const toIY = v => iAY+iAH-(v/maxD)*iAH;
          const curvePts=Array.from({length:60},(_,i)=>{
            const x=xLo+(xHi-xLo)*i/59;
            return `${toIX(x).toFixed(1)},${toIY(normpdf(x,mu,sig)).toFixed(1)}`;
          }).join(' ');
          // Shade ±1σ
          const shadePts=[
            `${toIX(mu-sig).toFixed(1)},${(iAY+iAH).toFixed(1)}`,
            ...Array.from({length:30},(_,i)=>{
              const x=(mu-sig)+(2*sig)*i/29;
              return `${toIX(x).toFixed(1)},${toIY(normpdf(x,mu,sig)).toFixed(1)}`;
            }),
            `${toIX(mu+sig).toFixed(1)},${(iAY+iAH).toFixed(1)}`,
          ].join(' ');

          return (
            <g key={ii}>
              <rect x={px} y={py} width={iW} height={iH} rx={3}
                fill={`${color}0d`} stroke={color} strokeWidth="0.8"/>
              <text x={px+40} y={py+iH/2+4} textAnchor="middle" fontSize="13"
                fill={color} fontWeight="700">{label}</text>
              <text x={px+40} y={py+iH/2+17} textAnchor="middle" fontSize="9" fill={color}>
                {mu}±{sig}
              </text>
              {/* Mini axes */}
              <line x1={iAX} y1={iAY+iAH} x2={iAX+iAW} y2={iAY+iAH} stroke="#ccc" strokeWidth="1"/>
              {/* Shade */}
              <polygon points={shadePts} fill={color} opacity="0.18"/>
              {/* Curve */}
              <polyline points={curvePts} fill="none" stroke={color} strokeWidth="1.8"/>
              {/* Mean tick */}
              <line x1={toIX(mu)} y1={iAY+iAH} x2={toIX(mu)} y2={iAY+iAH+4}
                stroke={color} strokeWidth="1.5"/>
              {/* ±1σ ticks */}
              {[mu-sig,mu+sig].map((v,vi)=>(
                <g key={vi}>
                  <line x1={toIX(v)} y1={iAY+iAH} x2={toIX(v)} y2={iAY+iAH+4}
                    stroke={color} strokeWidth="1" strokeDasharray="2,1"/>
                  <text x={toIX(v)} y={iAY+iAH+13} textAnchor="middle" fontSize="7.5" fill={color}>
                    {v.toFixed(1)}
                  </text>
                </g>
              ))}
              <text x={toIX(mu)} y={iAY+iAH+13} textAnchor="middle" fontSize="7.5" fill={color}>
                {mu}
              </text>
            </g>
          );
        })}

        {/* Arrow from inputs to output */}
        <text x={RP_X/2+LP_W/2} y={H/2+6} textAnchor="middle" fontSize="22" fill="#1F3864" opacity="0.3">→</text>
        <text x={RP_X/2+LP_W/2} y={H/2+22} textAnchor="middle" fontSize="10" fill="#1F3864" opacity="0.5">f=A·B/C</text>

        {/* RIGHT: output histogram */}
        <rect x={RP_X} y={8} width={RP_W} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <rect x={RP_X+4} y={12} width={RP_W-8} height={22} rx={3} fill="#9b59b6"/>
        <text x={RP_X+RP_W/2} y={27} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">
          Output Distribution: f = A·B/C
        </text>

        {/* Grid */}
        {[20,40,60,80].map(v=>(
          <g key={v}>
            <line x1={aX} y1={toY(v)} x2={aX+aW} y2={toY(v)} stroke="#eee" strokeWidth="0.8"/>
          </g>
        ))}
        <line x1={aX} y1={aY+aH} x2={aX+aW} y2={aY+aH} stroke="#aaa" strokeWidth="1.5"/>
        <line x1={aX} y1={aY}    x2={aX}    y2={aY+aH} stroke="#aaa" strokeWidth="1"/>

        {/* Histogram bars */}
        {histBins.map(({ lo, hi, count }, i) => {
          const bx=toX(lo)+0.5, bw=toX(hi)-toX(lo)-1;
          const bh=(count/maxCount)*aH, by=aY+aH-bh;
          return (
            <rect key={i} x={bx} y={by} width={bw} height={bh}
              fill="#9b59b6" opacity="0.55" rx={2}/>
          );
        })}

        {/* Normal overlay */}
        <polyline points={outCurvePts} fill="none" stroke="#9b59b6" strokeWidth="2.2"/>

        {/* Mean and ±σ lines */}
        <line x1={toX(muF)} y1={aY} x2={toX(muF)} y2={aY+aH}
          stroke="#c0392b" strokeWidth="2" strokeDasharray="4,3"/>
        <text x={toX(muF)+4} y={aY+14} fontSize="9.5" fill="#c0392b" fontWeight="700">
          μ_f={muF.toFixed(2)}
        </text>
        {[muF-sigF, muF+sigF].map((v,vi)=>(
          <g key={vi}>
            <line x1={toX(v)} y1={aY+4} x2={toX(v)} y2={aY+aH}
              stroke="#9b59b6" strokeWidth="1.5" strokeDasharray="4,3"/>
            <text x={toX(v)} y={aY+4} textAnchor="middle" fontSize="8.5" fill="#9b59b6"
              fontWeight="700">{vi===0?'-σ':'+σ'}</text>
          </g>
        ))}

        {/* X-axis ticks */}
        {Array.from({length:5},(_,i)=>{
          const v=fMin+(fMax-fMin)*i/4;
          return (
            <g key={i}>
              <line x1={toX(v)} y1={aY+aH} x2={toX(v)} y2={aY+aH+5} stroke="#aaa" strokeWidth="1"/>
              <text x={toX(v)} y={aY+aH+15} textAnchor="middle" fontSize="9" fill="#666">
                {v.toFixed(1)}
              </text>
            </g>
          );
        })}
        <text x={aX+aW/2} y={aY+aH+28} textAnchor="middle" fontSize="10" fill="#555">f = A·B/C</text>
        <text x={aX-36} y={aY+aH/2} textAnchor="middle" fontSize="9" fill="#555"
          transform={`rotate(-90,${aX-36},${aY+aH/2})`}>Frequency</text>

        {/* Summary box */}
        <rect x={aX} y={aY+aH+36} width={aW} height={30} rx={3} fill="#f5d0ff" stroke="#9b59b6"/>
        <text x={aX+aW/2} y={aY+aH+50} textAnchor="middle" fontSize="10"
          fill="#9b59b6" fontFamily="'Courier New',monospace" fontWeight="700">
          μ_f = {muF.toFixed(3)}   σ_f ≈ {sigF.toFixed(3)}   ({(sigF/muF*100).toFixed(1)}% relative uncertainty)
        </text>
        <text x={aX+aW/2} y={aY+aH+63} textAnchor="middle" fontSize="9" fill="#555">
          Taylor expansion: σ_f/f = √((σA/A)²+(σB/B)²+(σC/C)²)
        </text>
      </svg>
      <div style={S.dCaption}>
        Monte Carlo propagation samples each input variable from its distribution thousands of times, computes f for each sample set, and builds a histogram of outputs. This handles non-linear functions and non-Normal inputs without approximation. Here the analytical quadrature formula and the Monte Carlo simulation agree: σ_f/f = √(6²+6.7²+5²)% ≈ {(sigF/muF*100).toFixed(1)}%.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 7 — Significant Figures and Rounding
//             Left: number-line visual    Right: rules summary cards
// ─────────────────────────────────────────────────────────────────
function DiagramSignificantFigures() {
  const W = 900, H = 320;

  // Left: Show three "zoom levels" of a number line illustrating sig figs
  const levels = [
    { label:'3 sig. fig.  → 9.81',  center:9.81, span:0.01, tick:0.005, color:'#2E75B6',
      marked:[9.80,9.81,9.82], hilite:9.81 },
    { label:'2 sig. fig.  → 9.8',   center:9.81, span:0.1,  tick:0.05,  color:'#e07b00',
      marked:[9.7,9.8,9.9],    hilite:9.8  },
    { label:'1 sig. fig.  → 10',    center:9.81, span:1.0,  tick:0.5,   color:'#c0392b',
      marked:[9,10,11],        hilite:10   },
  ];

  const panH=(H-20)/3, lineY=panH/2;
  const lineL=340;

  const panX2=400, panW2=W-panX2-14;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Significant Figures — Rounding to Different Precision Levels (value = 9.812 m/s²)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* LEFT PANEL */}
        <rect x={10} y={8} width={380} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <rect x={14} y={12} width={372} height={22} rx={3} fill="#1F3864"/>
        <text x={204} y={27} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">
          Number line zoom — rounding 9.812 to fewer sig. figs.
        </text>

        {levels.map(({ label, center, span, tick, color, marked, hilite }, li) => {
          const py=36+li*panH;
          const lx=30, rx=lx+lineL;
          const mid=(lx+rx)/2;
          const toNX = v => mid+(v-center)/span*(lineL/2);

          return (
            <g key={li}>
              {/* Row background */}
              <rect x={14} y={py+2} width={372} height={panH-6} rx={3}
                fill={`${color}0a`} stroke={color} strokeWidth="0.6" opacity="0.6"/>

              {/* Level label */}
              <text x={20} y={py+panH/2+4} fontSize="10" fill={color} fontWeight="700">{label}</text>

              {/* Number line */}
              <line x1={lx} y1={py+lineY} x2={rx} y2={py+lineY}
                stroke="#ccc" strokeWidth="1.5"/>
              {/* Tick marks */}
              {Array.from({length:9},(_,i)=>{
                const v=center-span+(span/4)*i;
                const x=toNX(v);
                if(x<lx||x>rx) return null;
                return (
                  <g key={i}>
                    <line x1={x} y1={py+lineY-5} x2={x} y2={py+lineY+5}
                      stroke="#ddd" strokeWidth="1"/>
                    <text x={x} y={py+lineY+17} textAnchor="middle" fontSize="7.5" fill="#bbb">
                      {v.toFixed(span<0.05?3:span<0.5?2:0)}
                    </text>
                  </g>
                );
              })}
              {/* Major marked values */}
              {marked.map(v=>{
                const x=toNX(v);
                if(x<lx||x>rx) return null;
                return (
                  <g key={v}>
                    <line x1={x} y1={py+lineY-10} x2={x} y2={py+lineY+10}
                      stroke={color} strokeWidth="1.8"/>
                    <text x={x} y={py+lineY+22} textAnchor="middle" fontSize="9" fill={color}>
                      {span<0.05?v.toFixed(2):span<0.5?v.toFixed(1):v.toFixed(0)}
                    </text>
                  </g>
                );
              })}
              {/* True value dot */}
              <circle cx={toNX(9.812)} cy={py+lineY} r={5}
                fill="#1F3864" stroke="#fff" strokeWidth="1.2"/>
              <text x={toNX(9.812)} y={py+lineY-14} textAnchor="middle"
                fontSize="9" fill="#1F3864" fontWeight="700">9.812</text>

              {/* Rounded value highlight */}
              {(() => {
                const hx=toNX(hilite);
                if(hx<lx||hx>rx) return null;
                return (
                  <g>
                    <circle cx={hx} cy={py+lineY} r={7}
                      fill="none" stroke={color} strokeWidth="2.2"/>
                    <text x={hx} y={py+lineY-18} textAnchor="middle"
                      fontSize="10" fill={color} fontWeight="700">
                      rounded → {span<0.05?hilite.toFixed(2):span<0.5?hilite.toFixed(1):hilite.toFixed(0)}
                    </text>
                    {/* Uncertainty bracket */}
                    <line x1={hx-toNX(center+tick)+toNX(center)}
                          y1={py+lineY+10}
                          x2={hx+toNX(center+tick)-toNX(center)}
                          y2={py+lineY+10}
                          stroke={color} strokeWidth="1.2"/>
                    <text x={hx} y={py+lineY+24} textAnchor="middle"
                      fontSize="8" fill={color}>
                      ±{tick} last digit
                    </text>
                  </g>
                );
              })()}
            </g>
          );
        })}

        {/* RIGHT PANEL */}
        <rect x={panX2} y={8} width={panW2} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX2+panW2/2} y={26} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">
          Significant Figure Rules
        </text>

        {[
          { y:32,  bg:'#EBF3FB', bc:'#2E75B6', title:'Counting sig. figs.',
            lines:['All non-zero digits count','Zeros between sig. figs. count','Trailing zeros after decimal count','Leading zeros do NOT count'] },
          { y:128, bg:'#fff7ee', bc:'#e07b00', title:'Arithmetic rules',
            lines:['Add/subtract: match fewest','decimal places in result','Multiply/divide: match fewest','significant figures in result'] },
          { y:224, bg:'#f0fff4', bc:'#27ae60', title:'Uncertainty & sig. figs.',
            lines:['Report uncertainty to 1-2','significant figures; round','result to match last sig. fig.','of uncertainty (e.g. 9.81±0.03)'] },
        ].map(({ y, bg, bc, title, lines }) => (
          <g key={y}>
            <rect x={panX2+8} y={y} width={panW2-16} height={90} rx={4} fill={bg} stroke={bc} strokeWidth="1.3"/>
            <text x={panX2+18} y={y+16} fontSize="11" fill={bc} fontWeight="700">{title}</text>
            {lines.map((ln,j)=>(
              <text key={j} x={panX2+18} y={y+32+j*16} fontSize="10"
                fill={j<2?'#1F3864':'#555'}
                fontWeight={j<2?'600':'400'}>{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Significant figures represent the meaningful precision of a measurement. Rounding 9.812 to three sig. figs. gives 9.81 (uncertainty in last digit ≈ ±0.005); to two sig. figs. gives 9.8 (±0.05); to one sig. fig. gives 10 (±0.5). The result of a calculation should never appear more precise than the least precise measurement it was derived from.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function ErrorAnalysisUncertainty() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Error Analysis and Uncertainty</h1>

      <p style={S.p}>
        Every measurement is imperfect. Error analysis is the systematic study of how uncertainties in measured quantities arise, how large they are, and how they propagate through calculations to affect a final result. The goal is not to eliminate error — that is impossible — but to quantify it honestly and report results with appropriate confidence. A measurement without an uncertainty is incomplete: "g = 9.81 m/s²" is a statement of fact, but "g = 9.81 ± 0.03 m/s²" is a scientific result. This page covers the two fundamental types of error, how to quantify them statistically, how they propagate, and how to communicate them clearly.
      </p>

      <InfoBox
        title="Core Vocabulary of Error Analysis"
        items={[
          "True value: the exact value of the quantity being measured — usually unknown; approximated by the best estimate.",
          "Error: the difference between a measured value and the true value. Cannot be known exactly (if it could, it would be corrected).",
          "Uncertainty: a quantified estimate of the range within which the true value is expected to lie, at a stated confidence level.",
          "Systematic error (bias): reproducible, directional error due to a flawed instrument or method — not reduced by averaging.",
          "Random error: unpredictable fluctuations in each measurement — reduced by averaging repeated measurements.",
          "Precision: how repeatable measurements are (related to random error); Accuracy: how close to the true value (related to systematic error).",
          "Standard uncertainty u: one standard deviation of the measurement distribution; coverage factor k gives expanded uncertainty U = k·u.",
          "Propagation: how input uncertainties combine to produce output uncertainty when inputs feed into a calculation.",
        ]}
      />

      <h2 style={S.h2}>Systematic vs Random Error</h2>
      <DiagramTypesOfError />
      <Table2 rows={[
        ["Systematic error",     "Constant or slowly varying offset in one direction\nCauses: mis-calibrated instruments, zero offsets, environmental factors"],
        ["Random error",         "Unpredictable scatter around the true value\nCauses: readout resolution, thermal noise, human reaction time"],
        ["Reducing systematic",  "Calibration against a known reference standard\nBlind protocols, control groups, method validation"],
        ["Reducing random",      "Average n measurements: uncertainty in mean ∝ 1/√n\nUse higher-resolution instruments, control environmental conditions"],
        ["Accuracy vs precision","Accurate: mean of measurements is close to true value\nPrecise: measurements cluster tightly together (small SD)"],
        ["Trueness",             "ISO definition: closeness of mean of many measurements to true value\nDistinct from precision; a biased instrument can be precise but not true"],
      ]} />

      <h2 style={S.h2}>Distribution of Repeated Measurements</h2>
      <DiagramMeasurementDistribution />
      <Table2 rows={[
        ["Sample mean x̄",       "x̄ = (1/n)Σxᵢ — best estimate of the true value\nUnbiased: E[x̄] = μ regardless of distribution"],
        ["Sample std deviation s","s = √(Σ(xᵢ−x̄)²/(n−1)) — spread of individual measurements\nDenominator n−1 (Bessel's correction) makes s² unbiased for σ²"],
        ["Standard error SEM",   "SEM = s/√n — uncertainty in the mean x̄\nShrinks as √n; halving SEM requires 4× more measurements"],
        ["Reporting format",     "Result = x̄ ± u  where u is the chosen uncertainty\nAlways state: what u represents (SEM? SD? 95% CI?) and coverage factor k"],
        ["Type A uncertainty",   "Estimated by statistical analysis of repeated observations\nExample: SEM from n repeated measurements = s/√n"],
        ["Type B uncertainty",   "Estimated by non-statistical means: manufacturer specs, calibration cert.\nExample: ruler resolution → rectangular distribution, u = Δ/√3"],
      ]} />

      <h2 style={S.h2}>Uncertainty Propagation</h2>
      <DiagramErrorPropagation />
      <Table2 rows={[
        ["Addition / subtraction","f = A ± B  →  δf = √(δA² + δB²)\nAbsolute uncertainties combine in quadrature"],
        ["Multiplication / division","f = A·B or A/B  →  δf/f = √((δA/A)²+(δB/B)²)\nRelative uncertainties combine in quadrature"],
        ["Power law",             "f = Aⁿ  →  δf/f = |n|·δA/A\nExponents amplify relative uncertainty"],
        ["General formula",       "δf² = Σⱼ (∂f/∂xⱼ)²·δxⱼ² (independent inputs)\nPartial derivative = sensitivity coefficient for input xⱼ"],
        ["Correlated inputs",     "δf² = Σᵢ Σⱼ (∂f/∂xᵢ)(∂f/∂xⱼ)·Cov(xᵢ,xⱼ)\nCorrelated errors can add or partially cancel"],
        ["Worst-case (linear)",   "δf_max = Σⱼ |∂f/∂xⱼ|·δxⱼ — assumes all errors maximally aligned\nMore conservative than quadrature; used in safety-critical contexts"],
      ]} />

      <h2 style={S.h2}>Coverage Intervals</h2>
      <DiagramCoverageIntervals />
      <Table2 rows={[
        ["Standard uncertainty u","u = σ (or s for estimated σ) — corresponds to k=1, 68.27% coverage\nBasic building block; combine multiple u's before applying k"],
        ["Expanded uncertainty U","U = k·u — multiply by coverage factor k for wider interval\nk=2 gives ~95%, k=3 gives ~99.7% for Normal distributions"],
        ["Coverage probability",  "Fraction of measurements expected to fall within ±U\n68.27%, 95.45%, 99.73% for k=1,2,3 (Normal distribution)"],
        ["GUM framework",         "Guide to the Expression of Uncertainty in Measurement (ISO/IEC)\nStandard method for combining Type A and Type B uncertainties"],
        ["Effective degrees of freedom","νₑff from Welch–Satterthwaite equation for combined uncertainties\nDetermines correct t-factor when n is small"],
        ["Non-Normal distributions","Coverage factors differ for uniform, triangular distributions\nUniform: u = a/√3; triangular: u = a/√6 (a = half-width)"],
      ]} />

      <h2 style={S.h2}>Error Bars</h2>
      <DiagramErrorBars />
      <Table2 rows={[
        ["SD error bars",        "Show spread of individual data points, not uncertainty in mean\nWidth independent of n — do not shrink with more data"],
        ["SEM error bars",       "Show precision of the sample mean — width ∝ 1/√n\nShrinks as you add more data; most common in physiology papers"],
        ["95% CI error bars",    "Interval that contains the true mean with 95% frequency\nPreferred for inferential comparisons between groups"],
        ["Overlap rules (95% CI)","Non-overlapping 95% CIs → p < 0.05 approximately\nOverlapping CIs do NOT guarantee p > 0.05 (rule is asymmetric)"],
        ["Asymmetric error bars","Use for log-normal data, counts, or bounded quantities\nReport as [lower, upper] rather than ±δ"],
        ["Always label",         "Always state in figure legend which error bars are shown\nSD, SEM, and 95% CI bars look identical but have different meanings"],
      ]} />

      <h2 style={S.h2}>Monte Carlo Uncertainty Propagation</h2>
      <DiagramMonteCarlo />
      <Table2 rows={[
        ["Monte Carlo method",   "Sample each input xᵢ from its distribution N times\nCompute f(x₁,…,xₙ) for each sample; histogram the output"],
        ["Advantages",           "Handles non-linear functions without Taylor approximation\nWorks for any input distribution (non-Normal, correlated)"],
        ["Sample size",          "N = 10,000–100,000 samples typically sufficient\nConvergence checked by comparing results for different N"],
        ["Output statistics",    "Read μ_f, σ_f, percentiles directly from output histogram\nMedian and percentile intervals for skewed outputs"],
        ["Correlated inputs",    "Use Cholesky decomposition to generate correlated Normal inputs\nCopulas for non-Normal correlated distributions"],
        ["Comparison with GUM",  "Monte Carlo and GUM quadrature agree for linear-ish functions\nMC is preferred when nonlinearity is large or distributions are skewed"],
      ]} />

      <h2 style={S.h2}>Significant Figures and Rounding</h2>
      <DiagramSignificantFigures />
      <Table2 rows={[
        ["Significant figures",   "All digits known with confidence plus one uncertain digit\n9.81 has 3 sig. figs.; 9.810 has 4; 0.0098 has 2"],
        ["Rounding rule",         "If digit to be dropped is < 5: round down; ≥ 5: round up\nBanker's rounding (round half to even) reduces cumulative bias"],
        ["Addition/subtraction",  "Result has same number of decimal places as least precise input\n12.3 + 1.45 = 13.8 (not 13.75)"],
        ["Multiplication/division","Result has same sig. figs. as least precise input\n3.14 × 2.1 = 6.6 (not 6.594)"],
        ["Uncertainty alignment", "Round uncertainty to 1–2 sig. figs., then match decimal place\nResult: 9.812 ± 0.034 → report as 9.81 ± 0.03"],
        ["Intermediate values",   "Keep extra digits in intermediate calculations, round only at the end\nPremature rounding accumulates errors through a calculation"],
      ]} />

      <InfoBox
        title="Key Takeaways — Error Analysis and Uncertainty"
        items={[
          "Every measurement has two components: a best estimate and an uncertainty. A number without uncertainty is scientifically incomplete.",
          "Systematic error (bias) cannot be reduced by averaging — it must be identified and eliminated through calibration or experimental design.",
          "Random error decreases as 1/√n: to halve the uncertainty in the mean, you need four times as many measurements.",
          "Uncertainties from independent sources combine in quadrature: δf = √(δA² + δB²), never by simple addition.",
          "Report expanded uncertainty U = k·u with coverage factor: k=1 for 68% (standard), k=2 for ~95% (expanded), k=3 for ~99.7%.",
          "Error bars in figures must be labelled — SD, SEM, and 95% CI bars are visually identical but carry completely different meanings.",
          "Monte Carlo propagation handles non-linear functions and non-Normal inputs without approximation; use it when Taylor expansion is insufficient.",
          "Significant figures communicate precision: never report a result with more sig. figs. than justified by the uncertainty in the measurement.",
        ]}
      />
    </div>
  );
}